import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  BANNER_PRESETS,
  BANNER_ICONS,
  BANNER_ACTIONS,
  BANNER_AUDIENCES,
  BANNER_PLATFORMS,
  BannerActionType,
  BannerAudience,
  BannerPlatform,
  BannerPreset,
  BannerCustomStyle,
  BannerVisualOverride,
} from '../../../api/banners';
import { FormField, SegmentedControl } from '@/components/app';
import { cn } from '@/lib/utils';
import { BannerPreview } from './BannerPreview';
import { BannerColorEditor } from './BannerColorEditor';
import { BannerDialogProps } from '../types';
import { BRAND } from '@/lib/brand';

type Lang = 'uz' | 'ru' | 'kiril';
const LANGS: Array<{ key: Lang; label: string }> = [
  { key: 'uz', label: "O'zbekcha (lotin)" },
  { key: 'ru', label: 'Русский' },
  { key: 'kiril', label: 'Ўзбекча (кирилл)' },
];
const PREVIEW_LANGS = LANGS.map((l) => ({ value: l.key, label: l.label }));

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3.5 border-t border-border-soft pt-4 first:border-t-0 first:pt-0">
      <h3 className="m-0 font-display text-[15px] font-bold">{title}</h3>
      {children}
    </section>
  );
}

export function BannerDialog({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
  errors = {},
  onClearFieldError,
}: BannerDialogProps) {
  const [previewLang, setPreviewLang] = useState<Lang>('ru');
  const [showColorEditor, setShowColorEditor] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <DialogBody className="gap-5">
            <Section title="Содержание">
              {/* Название — только для админки */}
              <FormField label="Название" required error={errors.name} hint="Видно только в админке, пользователю не показывается">
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    onFormDataChange((prev) => ({ ...prev, name: e.target.value }));
                    onClearFieldError?.('name');
                  }}
                  placeholder="Скидка 30% — сентябрь"
                />
              </FormField>

              {/* Тексты по языкам */}
              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                <legend className="mb-1.5 text-[13px] font-semibold">
                  Заголовок<span className="ml-1 font-medium text-text-2">хотя бы на одном языке</span>
                </legend>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {LANGS.map(({ key, label }) => (
                    <FormField key={key} label={label} tone="filter">
                      <Input
                        value={formData.title?.[key] ?? ''}
                        aria-invalid={errors.title ? true : undefined}
                        onChange={(e) => {
                          onFormDataChange((prev) => ({ ...prev, title: { ...prev.title, [key]: e.target.value } }));
                          onClearFieldError?.('title');
                        }}
                      />
                    </FormField>
                  ))}
                </div>
                {errors.title && (
                  <span role="alert" className="text-[13px] font-semibold text-neg">{errors.title}</span>
                )}
              </fieldset>

              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                <legend className="mb-1.5 text-[13px] font-semibold">Подзаголовок</legend>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {LANGS.map(({ key, label }) => (
                    <FormField key={key} label={label} tone="filter">
                      <Input
                        value={formData.subtitle?.[key] ?? ''}
                        onChange={(e) =>
                          onFormDataChange((prev) => ({ ...prev, subtitle: { ...prev.subtitle, [key]: e.target.value } }))
                        }
                      />
                    </FormField>
                  ))}
                </div>
              </fieldset>
            </Section>

            <Section title="Оформление">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Пресет" required>
                  {(p) => (
                    <Select value={formData.preset} onValueChange={(value: BannerPreset) => onFormDataChange((prev) => ({ ...prev, preset: value }))}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANNER_PRESETS.map((pr) => (
                          <SelectItem key={pr.value} value={pr.value}>{pr.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
                <FormField label="Иконка" required>
                  {(p) => (
                    <Select value={formData.icon} onValueChange={(value: string) => onFormDataChange((prev) => ({ ...prev, icon: value }))}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANNER_ICONS.map((i) => (
                          <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
              </div>

              {/* Превью — ровно та вёрстка, что в приложении */}
              <div className="flex flex-col gap-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold">Как это будет выглядеть</span>
                  <SegmentedControl ariaLabel="Язык превью" value={previewLang} onChange={setPreviewLang} options={PREVIEW_LANGS} />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {([false, true] as const).map((dark) => (
                    <div key={String(dark)} className="flex flex-col gap-1">
                      <span className="text-xs text-text-2">{dark ? 'Тёмная тема' : 'Светлая тема'}</span>
                      <BannerPreview
                        preset={formData.preset}
                        icon={formData.icon}
                        title={formData.title}
                        subtitle={formData.subtitle}
                        showChevron={formData.action_type !== 'none'}
                        custom={formData.custom_style}
                        lang={previewLang}
                        dark={dark}
                      />
                    </div>
                  ))}
                </div>

                {/* Ручные цвета — по теме независимо */}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="self-start"
                  aria-expanded={showColorEditor}
                  onClick={() => setShowColorEditor((v) => !v)}
                >
                  <ChevronDown className={cn('transition-transform', showColorEditor && 'rotate-180')} aria-hidden />
                  {showColorEditor ? 'Скрыть ручную настройку цветов' : 'Настроить цвета вручную'}
                </Button>

                {showColorEditor && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {(['light', 'dark'] as const).map((mode) => (
                      <BannerColorEditor
                        key={mode}
                        mode={mode}
                        preset={formData.preset}
                        custom={formData.custom_style}
                        onChange={(next) =>
                          onFormDataChange((prev) => ({ ...prev, custom_style: mergeCustom(prev.custom_style, mode, next) }))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </Section>

            <Section title="Действие и показ">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Действие по тапу" required>
                  {(p) => (
                    <Select
                      value={formData.action_type}
                      onValueChange={(value: BannerActionType) => {
                        onFormDataChange((prev) => ({ ...prev, action_type: value }));
                        onClearFieldError?.('action_payload');
                      }}
                    >
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANNER_ACTIONS.map((a) => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
                {formData.action_type === 'external_url' && (
                  <FormField label="Ссылка" required error={errors.action_payload}>
                    <Input
                      type="url"
                      value={formData.action_payload ?? ''}
                      onChange={(e) => {
                        onFormDataChange((prev) => ({ ...prev, action_payload: e.target.value }));
                        onClearFieldError?.('action_payload');
                      }}
                      placeholder={BRAND.telegramChannelUrl}
                    />
                  </FormField>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Кому показывать">
                  {(p) => (
                    <Select value={formData.audience} onValueChange={(value: BannerAudience) => onFormDataChange((prev) => ({ ...prev, audience: value }))}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANNER_AUDIENCES.map((a) => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
                <FormField label="Платформа" hint="Баннеры про оплату картой на iOS лучше не показывать — правила Apple">
                  {(p) => (
                    <Select value={formData.platform} onValueChange={(value: BannerPlatform) => onFormDataChange((prev) => ({ ...prev, platform: value }))}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANNER_PLATFORMS.map((pl) => (
                          <SelectItem key={pl.value} value={pl.value}>{pl.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField label="Приоритет" hint="Больше — важнее. Показывается один баннер">
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={formData.priority ?? 0}
                    onChange={(e) => onFormDataChange((prev) => ({ ...prev, priority: Number(e.target.value) }))}
                  />
                </FormField>
                <FormField label="Начало показа" hint="Время Ташкента; пусто — сразу">
                  <Input
                    type="datetime-local"
                    value={formData.starts_at ?? ''}
                    onChange={(e) => onFormDataChange((prev) => ({ ...prev, starts_at: e.target.value || null }))}
                  />
                </FormField>
                <FormField label="Конец показа" hint="Время Ташкента; пусто — бессрочно" error={errors.ends_at}>
                  <Input
                    type="datetime-local"
                    value={formData.ends_at ?? ''}
                    onChange={(e) => {
                      onFormDataChange((prev) => ({ ...prev, ends_at: e.target.value || null }));
                      onClearFieldError?.('ends_at');
                    }}
                  />
                </FormField>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-xl bg-surface-alt px-3.5 py-3">
                <label htmlFor="banner-active" className="text-[13px] font-semibold">Баннер включён</label>
                <Switch
                  id="banner-active"
                  checked={formData.is_active ?? true}
                  onCheckedChange={(checked) => onFormDataChange((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
            </Section>
          </DialogBody>

          <DialogFooter bordered>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Отмена
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" aria-hidden />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Замена цветов одной темы. Если обе темы очищены — custom_style становится
 * null, чтобы в базе не оставался пустой объект.
 */
function mergeCustom(
  current: BannerCustomStyle | null | undefined,
  mode: 'light' | 'dark',
  next: BannerVisualOverride | null,
): BannerCustomStyle | null {
  const merged: BannerCustomStyle = { ...(current ?? {}), [mode]: next };
  if (!merged.light && !merged.dark) return null;
  return merged;
}
